import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

import { QuickCheckBoxFilter } from '@/components/new/table/filter/components/QuickCheckBoxFilter'
import { SelectionSummary } from '@/components/new/table/filter/SelectionSummary'
import { Form } from '@/components/ui/form'

import { useTableContext } from '../TableContext'
import { AdvancedFilters } from './AdvancedFilters'
import { SearchQuery } from './components/SearchQuery'

// const provinciaOptions = [
//   { value: 'habana', label: 'La Habana' },
//   { value: 'matanzas', label: 'Matanzas' },
//   { value: 'pinar', label: 'Pinar del Río' },
//   { value: 'artemisa', label: 'Artemisa' },
//   { value: 'mayabeque', label: 'Mayabeque' },
// ]

// const municipioOptions = [
//   { value: 'playa', label: 'Playa' },
//   { value: 'plaza', label: 'Plaza de la Revolución' },
//   { value: 'centro_habana', label: 'Centro Habana' },
//   { value: 'habana_vieja', label: 'Habana Vieja' },
//   { value: 'cerro', label: 'Cerro' },
// ]

// const dateTypeOptions = [
//   { value: 'emision', label: 'Fecha de emisión' },
//   { value: 'vencimiento', label: 'Fecha de vencimiento' },
//   { value: 'entrega', label: 'Fecha de entrega' },
//   { value: 'facturacion', label: 'Fecha de facturación' },
// ]

// const savedFilters = [
//   { id: '1', name: 'Pendientes de este mes' },
//   { id: '2', name: 'Facturas canceladas' },
//   { id: '3', name: 'Cliente: Desarrollo' },
// ]

// const statusOptions = [
//   { value: 'facturada', label: 'Facturada' },
//   { value: 'pendiente', label: 'Pendiente de pago' },
//   { value: 'creada', label: 'Creada' },
//   { value: 'vencido', label: 'Vencido' },
//   { value: 'reembolsado', label: 'Reembolsado' },
//   { value: 'cancelado', label: 'Cancelado' },
//   { value: 'activa', label: 'Activa' },
//   { value: 'cerrada', label: 'Cerrada' },
//   { value: 'despachada', label: 'Despachada' },
//   { value: 'procesando', label: 'Procesando' },
//   { value: 'completada', label: 'Completada' },
// ]

// Define Product interface based on ProductManager's usage if not already shared
interface Product {
  id: string
  name: string
  quantity: number
  unitPrice: number
  totalPrice: number
  currency: string
  stock?: number
  category?: string
  type?: string
  weight?: number
  volume?: number
}

// Define sub-interfaces for better type safety
interface NewClientData {
  name: string
  lastName?: string
  idNumber?: string
  phone?: string
  email?: string
  contractNumber?: string
  clientType?: string
  legalNotes?: string
  mainAddress?: string
  secondaryAddress?: string
  locality?: string
  country?: string
  province?: string
  municipality?: string
}

interface ReceiverInfo {
  name: string
  lastName: string
  phone?: string
  email?: string
  postalCode?: string
  notes?: string
}

interface NewAddressData {
  mainAddress?: string
  secondaryAddress?: string
  locality?: string
  country?: string // Default Cuba?
  province?: string
  municipality?: string
}

// Updated BillingFormData with defined sub-interfaces
export interface BillingFormData {
  // Client Info
  clientId?: string
  isNewClient?: boolean
  newClientData?: NewClientData
  clientNotes?: string

  // Document Details
  title?: string
  commercialId?: string
  paymentTerm?: string
  customDueDate?: Date
  calculatedDueDate?: Date | null // Store calculated date too
  observations?: string

  // Products
  products?: Product[] // Use specific Product interface

  // Delivery
  scheduleDelivery?: boolean
  receiverInfo?: ReceiverInfo
  deliveryAddressId?: string
  isNewAddress?: boolean
  newAddressData?: NewAddressData
  deliveryPersonId?: string
  deliveryDate?: Date
  deliveryCost?: number
  deliveryCostCurrency?: string
}

interface BasicFilterProps {
  type: string
  label: string
  icon?: React.ReactNode
}

interface CheckBoxProps extends BasicFilterProps {
  type: 'checkbox'
  label: string
  filterCode: string
  icon?: React.ReactNode
  options: { value: string; label: string }[]
}

// interface DatePickerRangeProps extends BasicFilterProps {
//   type: 'date'
//   label: string
//   icon?: React.ReactNode
//   dateFilterOptions: DateFilterOptions[]
// }

// interface QuickImportRangeFilterProps extends BasicFilterProps {
//   type: 'import-range'
//   label: string
//   icon?: React.ReactNode
//   filterCodes: {
//     min: string
//     max: string
//   }
// }

export type FilterTable = CheckBoxProps

export const FiltersRow = () => {
  const { search, quickFilterList, advancedFilterList } = useTableContext()
  const [advancedFilters, setAdvancedFilters] = useState<
    Record<string, string>
  >({})
  const [quickFilters, setQuickFilters] = useState<Record<string, string>>({})
  const [filter, setFilter] = useState<Record<string, string>>({})
  const [, setResetTrigger] = useState<number>(0)
  const quickFilterForm = useForm()

  // Efecto para sincronizar el estado del formulario con los filtros
  useEffect(() => {
    // Resetear controles que ya no están en quickFilters
    quickFilterList?.list?.forEach((filterItem) => {
      const isFilterActive = Object.keys(quickFilters).includes(
        filterItem.label
      )
      if (!isFilterActive) {
        const fieldName = filterItem.filterCode
        quickFilterForm.setValue(
          fieldName,
          filterItem.type === 'checkbox' ? [] : undefined
        )
      }
    })
  }, [quickFilters, quickFilterList, quickFilterForm])
  const setNewAdvancedFilters = (newFilters: Record<string, string>) => {
    setAdvancedFilters(newFilters)
    setFilter({
      ...newFilters,
      ...quickFilters,
    })
  }

  const setNewQuickFilters = (newFilters: Record<string, string>) => {
    setQuickFilters(newFilters)
    setFilter({
      ...advancedFilters,
      ...newFilters,
    })
  }

  const removeFilter = (filterCode: string) => {
    const nextFilter = Object.fromEntries(
      Object.entries(filter).filter(([k]) => k !== filterCode)
    )
    setFilter(nextFilter)

    // Update advanced filters
    const nextAdvanced = Object.fromEntries(
      Object.entries(advancedFilters).filter(([k]) => k !== filterCode)
    )
    setAdvancedFilters(nextAdvanced)

    // Update quick filters
    const nextQuick = Object.fromEntries(
      Object.entries(quickFilters).filter(([k]) => k !== filterCode)
    )
    setQuickFilters(nextQuick)

    // Reset the form control for the removed filter
    const removedQuickFilter = quickFilterList?.list?.find(
      (item) => item.label === filterCode
    )
    if (removedQuickFilter) {
      quickFilterForm.setValue(removedQuickFilter.filterCode || 'date', [])
    }

    // Call handleFormSubmit to trigger form submission after removing the filter
    handleFormSubmit()
  }

  const removeAllFilters = () => {
    setFilter({})
    setAdvancedFilters({})
    setQuickFilters({})

    // Reset all quick filter form controls
    quickFilterForm.reset()

    // Trigger reset for all import-range filters
    setResetTrigger((prev) => prev + 1)

    // Call handleFormSubmit to trigger form submission after clearing all filters
    handleFormSubmit()
  }

  const onChangeFilter = (values: { displayName: string; value: string }[]) => {
    let nextQuickFilters = { ...quickFilters }
    values.forEach((value) => {
      if (value.value === '') {
        // Remove filter if value is empty
        nextQuickFilters = Object.fromEntries(
          Object.entries(nextQuickFilters).filter(
            ([k]) => k !== value.displayName
          )
        )
      } else {
        // Set filter with label as key and joined option labels as value
        nextQuickFilters[value.displayName] = value.value
      }
    })
    setNewQuickFilters(nextQuickFilters)
  }

  const handleFormSubmit = () => {
    quickFilterForm.handleSubmit((data) => {
      quickFilterList?.onChange(data)
    })()
  }

  return (
    <div className="mb-2">
      <div className="flex flex-col gap-4">
        <Form {...quickFilterForm}>
          <div className="flex flex-wrap items-center gap-2">
            {search && (
              <SearchQuery
                onChange={search.action}
                placeholder={search.placeholder ?? 'Buscar...'}
              />
            )}

            {(quickFilterList?.list ?? []).map((item) => {
              switch (item.type) {
                case 'checkbox':
                  return (
                    <QuickCheckBoxFilter
                      control={quickFilterForm.control}
                      icon={item.icon}
                      key={item.filterCode}
                      label={item.label}
                      name={item.filterCode}
                      onChange={onChangeFilter}
                      onFormSubmit={handleFormSubmit}
                      options={item.options ?? []}
                    />
                  )
              }
            })}
            {advancedFilterList && (
              <AdvancedFilters
                selectedFilters={advancedFilters}
                setSelectedFilters={setNewAdvancedFilters}
              />
            )}
          </div>
        </Form>

        {Object.keys(filter).length > 0 && (
          <SelectionSummary
            filter={filter}
            removeAllFilters={removeAllFilters}
            removeFilter={removeFilter}
          />
        )}
      </div>
    </div>
  )
}
